import React, { useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  Plus,
  Loader2,
  RefreshCw,
  ExternalLink,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronRight,
  Download,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const STATUS_STYLE = {
  ready: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30',
  pending: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30',
  error: 'bg-destructive/15 text-destructive border border-destructive/30',
};

function StatusBadge({ status }) {
  const Icon = status === 'ready' ? CheckCircle2 : status === 'error' ? AlertCircle : Clock;
  return (
    <Badge className={`font-mono-edge text-[10px] ${STATUS_STYLE[status] || ''}`}>
      <Icon size={10} className="mr-1" /> {status}
    </Badge>
  );
}

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    return format(new Date(ts), 'MMM d, HH:mm');
  } catch (_e) {
    return ts;
  }
}

export function AdminInspirationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deckBusyId, setDeckBusyId] = useState(null);

  const buildParams = () => {
    const p = {};
    if (statusFilter) p.status = statusFilter;
    if (q.trim()) p.q = q.trim();
    return p;
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listInspirationActivity(buildParams());
      setItems(r?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load inspiration activity'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownloadDeck = async (row) => {
    const key = row.analysis_id;
    if (!key) return;
    setDeckBusyId(key);
    const t = toast.loading(`Building strategic positioning deck for ${row.subject_name || 'audit'}… (30-90s if first run)`);
    try {
      const token = localStorage.getItem('le_token');
      const base = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const res = await fetch(
        `${base}/analyze/${row.analysis_id}/inspirations/positioning-deck.pptx`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        let msg = `Failed (${res.status})`;
        try { const j = await res.json(); msg = j?.detail?.message || j?.detail || msg; } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (row.subject_name || row.analysis_id || 'audit').toString().replace(/\s+/g, '-').toLowerCase().slice(0, 40);
      a.href = url;
      a.download = `positioning-report-${slug}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Deck downloaded.', { id: t });
    } catch (err) {
      toast.error(err?.message || 'Deck failed', { id: t });
    } finally {
      setDeckBusyId(null);
    }
  };

  const onRescrape = async (row) => {
    setBusyId(`${row.analysis_id}:${row.slug}`);
    const t = toast.loading(`Re-scraping ${row.leader_name}…`);
    try {
      const r = await adminApi.rescrapeInspiration({ analysisId: row.analysis_id, slug: row.slug });
      // Replace the row in state
      setItems((prev) =>
        prev.map((x) =>
          x.analysis_id === row.analysis_id && x.slug === row.slug
            ? {
                ...x,
                scrape_status: r.pin?.scrape_status || 'pending',
                scrape_error: (r.pin?.scrape_error || '').slice(0, 140),
                scraped_at: r.pin?.scraped_at,
                post_count: (r.pin?.posts || []).length,
                total_reactions: (r.pin?.posts || []).reduce((s, p) => s + (Number(p.likes) || 0), 0),
                total_comments: (r.pin?.posts || []).reduce((s, p) => s + (Number(p.comments) || 0), 0),
                topic_count: (r.pin?.topic_clusters || []).length,
              }
            : x,
        ),
      );
      toast.success('Re-scrape complete', { id: t });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Re-scrape failed'), { id: t });
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    const out = { ready: 0, pending: 0, error: 0, posts: 0, leaders: items.length };
    for (const r of items) {
      out[r.scrape_status] = (out[r.scrape_status] || 0) + 1;
      out.posts += r.post_count || 0;
    }
    return out;
  }, [items]);

  return (
    <Card className="border-border" data-testid="admin-inspirations-panel">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <Sparkles size={18} className="text-primary" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold">Inspiration leaders &amp; benchmarks</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add LinkedIn URLs of leaders to any analysis (admin override bypasses the user&apos;s
              5/month quota), watch live scraping activity, and re-scrape failed or stale leaders.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={reload}
            disabled={loading}
            data-testid="admin-insp-refresh-btn"
          >
            {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setAddOpen(true)}
            className="rounded-xl"
            data-testid="admin-insp-add-btn"
          >
            <Plus size={12} className="mr-1.5" /> Add leader
          </Button>
        </div>

        {/* Stats row */}
        <div className="px-4 py-3 border-b border-border grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Stat label="Leaders" value={stats.leaders} />
          <Stat label="Ready" value={stats.ready || 0} accent="success" />
          <Stat label="Pending" value={stats.pending || 0} accent="warning" />
          <Stat label="Errors" value={stats.error || 0} accent="destructive" />
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="admin-insp-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs" htmlFor="ai-q">Search</Label>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="ai-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="leader name / subject name"
                className="pl-7"
                data-testid="admin-insp-q-filter"
                onKeyDown={(e) => e.key === 'Enter' && reload()}
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={reload} disabled={loading} className="rounded-xl w-full" data-testid="admin-insp-apply-btn">
              Apply
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading && items.length === 0 ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="admin-insp-empty">
            <Sparkles size={24} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No inspiration leaders pinned yet. Click <span className="font-mono-edge">Add leader</span> to
              benchmark an analysis against a LinkedIn URL.
            </p>
          </div>
        ) : (
          <Table data-testid="admin-insp-table">
            <TableHeader>
              <TableRow>
                <TableHead>Leader</TableHead>
                <TableHead>For analysis</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Posts</TableHead>
                <TableHead className="text-right">Reactions</TableHead>
                <TableHead className="text-right">Topics</TableHead>
                <TableHead>Scraped</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row, idx) => {
                const rowKey = `${row.analysis_id}:${row.slug || idx}`;
                const busy = busyId === rowKey;
                return (
                  <TableRow key={rowKey} data-testid={`admin-insp-row-${row.slug || idx}`}>
                    <TableCell className="text-xs">
                      <div className="font-medium truncate max-w-[180px]" title={row.leader_name}>
                        {row.leader_name || '—'}
                      </div>
                      {row.linkedin_url && (
                        <a
                          href={row.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline text-[10px] font-mono-edge"
                          data-testid="admin-insp-link"
                        >
                          {row.slug || row.linkedin_url} <ExternalLink size={9} />
                        </a>
                      )}
                      {row.source === 'admin' && (
                        <Badge className="ml-1 bg-primary/10 text-primary border border-primary/30 font-mono-edge text-[9px]">
                          admin
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium truncate max-w-[160px]" title={row.subject_name}>
                        {row.subject_name}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[160px]" title={row.user_email || ''}>
                        {row.user_email || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.scrape_status} />
                      {row.scrape_error && (
                        <p className="text-[10px] text-destructive mt-1 truncate max-w-[180px]" title={row.scrape_error}>
                          {row.scrape_error}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono-edge text-xs">{row.post_count}</TableCell>
                    <TableCell className="text-right font-mono-edge text-xs">{row.total_reactions?.toLocaleString() || 0}</TableCell>
                    <TableCell className="text-right font-mono-edge text-xs">{row.topic_count}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtTs(row.scraped_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDownloadDeck(row)}
                          disabled={deckBusyId === row.analysis_id || row.scrape_status !== 'ready'}
                          title={row.scrape_status !== 'ready' ? 'Deck available once at least one leader is ready' : 'Download strategic positioning deck (auto-generated on first run, 30-90s)'}
                          data-testid={`admin-insp-deck-${row.slug || idx}`}
                          className="border-primary/40 text-primary hover:bg-primary/5"
                        >
                          {deckBusyId === row.analysis_id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <Download size={11} className="mr-1" />
                          )}
                          Deck
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRescrape(row)}
                          disabled={busy || !row.slug}
                          data-testid={`admin-insp-rescrape-${row.slug || idx}`}
                        >
                          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} className="mr-1" />}
                          Re-scrape
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {addOpen && (
        <AddLeaderDialog
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            reload();
          }}
        />
      )}
    </Card>
  );
}

function Stat({ label, value, accent }) {
  const cls =
    accent === 'success'
      ? 'text-[hsl(var(--success))]'
      : accent === 'warning'
      ? 'text-[hsl(var(--warning))]'
      : accent === 'destructive'
      ? 'text-destructive'
      : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
      <p className="text-[10px] font-mono-edge text-muted-foreground uppercase tracking-[0.14em]">{label}</p>
      <p className={`text-lg font-semibold ${cls}`}>{value}</p>
    </div>
  );
}

function AddLeaderDialog({ onClose, onSaved }) {
  const [analyses, setAnalyses] = useState([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnalysisId, setSelectedAnalysisId] = useState('');
  const [leaderName, setLeaderName] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [scrapeNow, setScrapeNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadAnalyses = async (qVal = '') => {
    setLoadingAnalyses(true);
    try {
      const r = await adminApi.listAdminAnalyses({ q: qVal || undefined, page_size: 50 });
      setAnalyses(r?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load analyses'));
    } finally {
      setLoadingAnalyses(false);
    }
  };

  useEffect(() => {
    loadAnalyses();
  }, []);

  const onSearch = (e) => {
    e?.preventDefault?.();
    loadAnalyses(searchTerm);
  };

  const onSubmit = async () => {
    if (!selectedAnalysisId) {
      toast.error('Pick an analysis');
      return;
    }
    if (!leaderName.trim()) {
      toast.error('Leader name is required');
      return;
    }
    if (!linkedinUrl.trim()) {
      toast.error('LinkedIn URL is required');
      return;
    }
    setSubmitting(true);
    const t = toast.loading(scrapeNow ? 'Adding & scraping leader…' : 'Adding leader…');
    try {
      await adminApi.addInspiration({
        analysisId: selectedAnalysisId,
        leaderName: leaderName.trim(),
        linkedinUrl: linkedinUrl.trim(),
        scrapeNow,
      });
      toast.success(`Added ${leaderName.trim()}.`, { id: t });
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not add leader'), { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="admin-insp-add-dialog">
        <DialogHeader>
          <DialogTitle>Add inspiration leader to an analysis</DialogTitle>
          <DialogDescription>
            Pin any LinkedIn profile to a target analysis. Optionally scrape their last 5 posts
            immediately — admin override bypasses the user&apos;s monthly quota.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Analysis picker */}
          <div className="space-y-1.5">
            <Label className="text-xs">Target analysis</Label>
            <form onSubmit={onSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="search by subject name / email / ID"
                  className="pl-7"
                  data-testid="admin-insp-add-search"
                />
              </div>
              <Button type="submit" variant="outline" size="sm" disabled={loadingAnalyses} data-testid="admin-insp-add-search-btn">
                {loadingAnalyses ? <Loader2 size={12} className="animate-spin" /> : 'Search'}
              </Button>
            </form>
            <div className="border border-border rounded-lg max-h-56 overflow-y-auto">
              {loadingAnalyses && analyses.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 size={11} className="animate-spin" /> Loading analyses…
                </div>
              ) : analyses.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground">No analyses match.</div>
              ) : (
                <ul className="divide-y divide-border" data-testid="admin-insp-analysis-list">
                  {analyses.map((a) => {
                    const sel = a.id === selectedAnalysisId;
                    return (
                      <li
                        key={a.id}
                        className={`px-3 py-2 cursor-pointer transition-colors text-xs flex items-center gap-2 ${
                          sel ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-secondary/40'
                        }`}
                        onClick={() => setSelectedAnalysisId(a.id)}
                        data-testid={`admin-insp-analysis-${a.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium truncate max-w-[220px]" title={a.subject_name}>
                              {a.subject_name}
                            </span>
                            {a.industry && (
                              <Badge variant="secondary" className="text-[9px] font-mono-edge">{a.industry}</Badge>
                            )}
                            {a.pin_count > 0 && (
                              <Badge variant="outline" className="text-[9px] font-mono-edge">
                                {a.ready_pin_count}/{a.pin_count} pinned
                              </Badge>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate" title={a.user_email}>
                            {a.user_email || '—'} · {fmtTs(a.created_at)}
                          </div>
                        </div>
                        {sel && <ChevronRight size={12} className="text-primary" />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="lead-name">Leader name</Label>
              <Input
                id="lead-name"
                value={leaderName}
                onChange={(e) => setLeaderName(e.target.value)}
                placeholder="e.g. Satya Nadella"
                data-testid="admin-insp-name-input"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="lead-url">LinkedIn URL</Label>
              <Input
                id="lead-url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/satya-nadella/"
                data-testid="admin-insp-url-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Switch
              checked={scrapeNow}
              onCheckedChange={setScrapeNow}
              id="scrape-now"
              data-testid="admin-insp-scrape-now-switch"
            />
            <Label htmlFor="scrape-now" className="text-sm cursor-pointer">
              Scrape immediately (bypasses user&apos;s 5/month quota)
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={submitting} className="rounded-xl" data-testid="admin-insp-submit-btn">
            {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
            Add leader
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdminInspirationsPanel;
